import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactElement } from 'react';
import { useTranslations } from 'i18n/use-translations';
import { Button as FlowbiteButton, Dropdown, type FlowbiteDropdownTheme } from 'flowbite-react';
import type { ComponentSize, SelectMenuItem } from './__commons__/selectCommons';
import {
    itemContainerClass,
    contentContainerClass,
    disabledItemClass,
    enabledItemClass,
} from './__commons__/selectCommons';
import { dropdownTheme } from '../../styles/themes/select';
import { Text, TextProps } from '../text';
import { buttonIconTheme, selectButtonTheme } from '../../styles/themes/button';
import { IconLoader } from '../iconLoader';
import { Input, type InputProps } from '../input';
import { DropdownItem } from '../dropdown';
import { SelectItemContent } from './select-item-content';
import { CheckIndicator } from '../check-indicator';
import { SelectEmptyGroup } from './select-empty-group';
import { ContextualDropdown } from './contextual-dropdown';
import { SelectSearchStatus } from './select-search-status';
import { VisuallyHidden } from '../visually-hidden';
import { getSelectTriggerId } from './select-trigger-id';

export type DropdownPlacement = 'top' | 'bottom' | 'bottom-end' | 'left' | 'right' | 'left-start' | 'right-start';

export interface SelectProps {
    id: string;
    value?: string;
    valueClassName?: string;
    valueVariant?: TextProps['variant'];
    middleMenuClassName?: string;
    middleMenuItems: SelectMenuItem[];
    middleMenuItemsClassName?: string;
    //inline styles for the middle menu container
    middleMenuStyle?: React.CSSProperties;

    bottomMenuItems?: SelectMenuItem[];
    label?: string;
    /** Keeps `label` as the accessible name but hides it visually. */
    hideLabel?: boolean;
    /** Shows a red * after the label and adds "Required" to the trigger's name. Default mode only. */
    labelRequiredSymbol?: boolean;
    helper?: string;
    /** Field error. Rendered by the default and 'portal' modes; 'contextual' mode drops it. */
    errorMessage?: string;
    /** Error styling without a message, for a caller that explains the error elsewhere. Same modes as `errorMessage`. */
    invalid?: boolean;
    /** In 'portal' mode: hidden context prefix for the trigger's name. In 'contextual' mode: the field's name. */
    ariaLabel?: string;
    placement?: DropdownPlacement;
    testId?: string;
    displaySelectedIcon?: boolean;
    enableActions?: boolean;
    /**
     * Trigger look. Default mode: a colour key of `selectButtonTheme` ('primary', 'secondary', 'outline',
     * 'destruct', 'ghost'). 'portal' mode: 'ghost' is a borderless trigger that reads as a table cell's text, and
     * any other value is the field trigger. 'contextual' mode and `renderTrigger` ignore it.
     */
    variant?: string;
    size?: ComponentSize;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    disabled?: boolean;
    header?: header;
    onItemSelect: (item: SelectMenuItem) => void;
    /** Contextual and portal search only: called after Escape closes the open menu. */
    onEscape?: () => void;
    /** The items run commands rather than make a choice, so they are plain menuitems with no checked state. */
    actionMenu?: boolean;
    pill?: boolean;

    arrow?: boolean;
    /** Extra classes for the trigger's chevron, e.g. to show it only while its container is hovered. */
    arrowClassName?: string;
    renderTrigger?: () => ReactElement;
    triggerLabelOverride?: React.ReactNode;
    buttonClassName?: string;

    onClear?: () => void;
    // Accessible name for the trigger's clear control; without it the icon is pointer-only
    clearLabel?: string;
    multiSelect?: boolean;
    onSelectAll?: (items: SelectMenuItem[]) => void;
    disableSelectOptions?: boolean;

    /**
     * true: inline menu with a search field. 'contextual': the trigger is the search field, menu portalled.
     * 'portal': button trigger with the search field in the portalled menu; use inside overflow containers.
     */
    searchable?: boolean | 'contextual' | 'portal';
    /** The search term. Leave it unset and the Select holds the term itself; `onSearchChange` still reports it. */
    searchValue?: string;
    onSearchChange?: (term: string) => void;
    searchFunction?: (item: SelectMenuItem, term: string) => boolean;
    searchHeaderBorderClassName?: string;
    // Accessible name for the search field's clear control; defaults to the translated "Clear"
    searchClearLabel?: string;
    searchLabel?: string;
    placeholder?: string;

    inputProps?: Partial<InputProps>;

    groupBy?: (item: SelectMenuItem) => string;
    groupOrder?: string[];
    renderGroupHeader?: (groupKey: string) => React.ReactNode;
    groupHeaderClassName?: string;
    groupHeaderTextClassName?: string;
    groupHeaderBorderClassName?: string;
    // Message shown beneath a group header when that group has no items. Exposed for translation.
    emptyGroupMessage?: string;

    dropdownClassName?: string;
    //inline styles for the dropdown container
    dropdownStyle?: React.CSSProperties;

    dropdownThemeOverride?: FlowbiteDropdownTheme;
}

export interface header {
    title: string;
    subtitle?: string;
}

export const Select: React.FC<SelectProps> = ({
    id,
    value,
    valueClassName,
    valueVariant,
    middleMenuItems,
    bottomMenuItems = [],
    middleMenuClassName = '',
    middleMenuItemsClassName = '',

    label,
    hideLabel = false,
    labelRequiredSymbol = false,
    helper,
    errorMessage,
    invalid = false,
    ariaLabel,
    displaySelectedIcon = true,
    placement = 'bottom',
    testId,
    disabled = false,
    variant = 'primary',
    size = 'sm',
    header,
    onItemSelect,
    onEscape,
    actionMenu = false,
    enableActions = false,
    startIcon,
    endIcon,
    pill = false,
    arrow = true,
    arrowClassName,
    renderTrigger,
    onClear,
    clearLabel,
    buttonClassName,
    triggerLabelOverride,

    multiSelect = false,
    onSelectAll,
    disableSelectOptions = false,
    searchable = false,
    searchValue: controlledSearchValue,
    onSearchChange,
    searchFunction = (item, term) =>
        [item.value, item.label, ...(item.tags?.map((tag) => tag.label) ?? [])].some((str) =>
            str?.toLowerCase().includes(term.toLowerCase()),
        ),
    searchHeaderBorderClassName = '',
    searchClearLabel,
    searchLabel,
    placeholder,

    inputProps,

    groupBy,
    groupOrder = [],
    renderGroupHeader,
    groupHeaderClassName = '',
    groupHeaderTextClassName = '',
    groupHeaderBorderClassName = '',
    emptyGroupMessage,

    dropdownClassName = '',
    dropdownStyle,
    middleMenuStyle,
    dropdownThemeOverride,
}) => {
    const { __ } = useTranslations();
    const resolvedEmptyGroupMessage = emptyGroupMessage ?? __('misc.no_results_in_category');
    const resolvedSearchClearLabel = searchClearLabel ?? __('misc.clear');
    // Host for the inline search status: the menu's role allows no status child
    const [statusHost, setStatusHost] = useState<HTMLDivElement | null>(null);
    const [uncontrolledSearchValue, setUncontrolledSearchValue] = useState('');
    const isSearchControlled = controlledSearchValue !== undefined;
    const searchValue = isSearchControlled ? controlledSearchValue : uncontrolledSearchValue;
    const handleSearchChange = useCallback(
        (term: string) => {
            if (!isSearchControlled) {
                setUncontrolledSearchValue(term);
            }
            onSearchChange?.(term);
        },
        [isSearchControlled, onSearchChange],
    );
    const searchTerm = searchable ? searchValue : '';
    // Disabled options cannot be toggled, so they do not count towards "all"
    const selectableItems = middleMenuItems.filter((i) => !i.isDisabled);
    const allSelected = selectableItems.length > 0 && selectableItems.every((i) => i.isSelected);
    const hasSelectedItems = middleMenuItems.some((i) => i.isSelected);

    const filteredItems = searchable ? middleMenuItems.filter((i) => searchFunction(i, searchTerm)) : middleMenuItems;

    const groups: Record<string, SelectMenuItem[]> = {};
    filteredItems.forEach((item) => {
        const key = groupBy ? groupBy(item) : '';
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(item);
    });
    const groupKeys = groupBy ? [...groupOrder, ...Object.keys(groups).filter((k) => !groupOrder.includes(k))] : [''];

    const isSelectAllDisabled =
        selectableItems.length === 0 || (disableSelectOptions && middleMenuItems.every((i) => !i.isSelected));

    const handleSelectAll = useCallback(() => {
        const shouldSelectAll = !allSelected;
        const updated = middleMenuItems.map((item) => {
            const isDisabled = (disableSelectOptions && !item.isSelected) || item.isDisabled;
            if (isDisabled) {
                return item;
            }
            return { ...item, isSelected: shouldSelectAll };
        });
        onSelectAll?.(updated);
    }, [allSelected, disableSelectOptions, middleMenuItems, onSelectAll]);

    const renderArrow = useCallback(
        (button: HTMLButtonElement | null) => {
            const className = `stroke-system-on-surface-variant fill-system-on-surface-variant ${arrowClassName ?? ''}`;
            const expanded = button?.getAttribute('aria-expanded') === 'true';

            const [key] = placement.split('-') as ['bottom' | 'top' | 'left' | 'right'];
            let iconName: 'IconChevronUp' | 'IconChevronDown' | 'IconChevronLeft' | 'IconChevronRight';

            switch (key) {
                case 'bottom':
                    iconName = expanded ? 'IconChevronUp' : 'IconChevronDown';
                    break;
                case 'top':
                    iconName = expanded ? 'IconChevronDown' : 'IconChevronUp';
                    break;
                case 'left':
                    iconName = expanded ? 'IconChevronRight' : 'IconChevronLeft';
                    break;
                case 'right':
                    iconName = expanded ? 'IconChevronLeft' : 'IconChevronRight';
                    break;
            }

            return <IconLoader className={className} size="sm" iconName={iconName} />;
        },
        [placement, arrowClassName],
    );

    const selectedItems = middleMenuItems.filter((i) => i.isSelected);
    const defaultSummary = selectedItems.length ? selectedItems.map((i) => i.value).join(', ') : undefined;

    const triggerContent = triggerLabelOverride ?? (multiSelect ? defaultSummary ?? value ?? null : value ?? null);
    const errorId = `${id}-error`;
    const labelId = `${id}-label`;

    const dropdownTrigger = useCallback(() => {
        const triggerId = getSelectTriggerId(id);
        const button = document.getElementById(triggerId);

        return (
            <FlowbiteButton
                pill={pill}
                id={triggerId}
                aria-label={ariaLabel}
                // name the trigger by label + current value ("Venue, Stadium A")
                aria-labelledby={!ariaLabel && label ? `${labelId} ${triggerId}` : undefined}
                data-testid={testId}
                size={size}
                color={variant}
                theme={pill ? buttonIconTheme : selectButtonTheme}
                className={errorMessage || invalid ? `${buttonClassName ?? ''} !border-system-error` : buttonClassName}
                aria-invalid={errorMessage || invalid ? true : undefined}
                aria-describedby={errorMessage ? errorId : undefined}
            >
                {startIcon && <div className="flex flex-shrink-0">{startIcon}</div>}
                {/* min-h-[1lh] keeps the trigger height stable: with no value there is no
                    text, so the line box collapses and the trigger grows on first selection */}
                <Text
                    variant={valueVariant ?? 'body-large'}
                    className={`flex-1 text-left truncate min-h-[1lh] ${valueClassName ?? ''}`}
                >
                    {triggerContent}
                </Text>
                <div className="flex items-center gap-1">
                    {onClear && hasSelectedItems && (
                        // The trigger is already a button, so this cannot be a nested <button>; with a
                        // label it becomes a focusable ARIA button instead of a pointer-only icon.
                        <span
                            role={clearLabel ? 'button' : undefined}
                            tabIndex={clearLabel ? 0 : undefined}
                            aria-label={clearLabel}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClear();
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    // Contained, or the trigger button underneath would also activate.
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClear();
                                }
                            }}
                            className="flex items-center cursor-pointer"
                        >
                            <IconLoader size="sm" iconName="IconRemove" className="stroke-system-on-surface-variant" />
                        </span>
                    )}
                    {endIcon}
                    {arrow && renderArrow(button as HTMLButtonElement)}
                </div>
            </FlowbiteButton>
        );
    }, [
        id,
        pill,
        size,
        variant,
        testId,
        startIcon,
        valueVariant,
        valueClassName,
        hasSelectedItems,
        triggerContent,
        onClear,
        clearLabel,
        endIcon,
        arrow,
        renderArrow,
        buttonClassName,
        ariaLabel,
        label,
        labelId,
        errorMessage,
        invalid,
        errorId,
    ]);

    const isContextual = searchable === 'contextual' || searchable === 'portal';
    const resolvedTheme = dropdownThemeOverride ?? dropdownTheme;

    const handleItemClick = useCallback(
        (item: SelectMenuItem) => {
            const itemDisabled = (disableSelectOptions && !item.isSelected) || item.isDisabled;
            if (itemDisabled) {
                return;
            }
            const selected = { ...item, isSelected: !item.isSelected };
            if (actionMenu) {
                // An action often opens a dialog. The closing menu hands focus back to its trigger in a
                // microtask, so acting a task later lets that land first and the dialog then takes focus
                // from the trigger, instead of the menu stealing it back from the open dialog.
                setTimeout(() => onItemSelect(selected), 0);
            } else {
                onItemSelect(selected);
            }
            // synchronous: an item's own onClick may open a window, which needs the click's user gesture
            item.onClick?.();
        },
        [disableSelectOptions, onItemSelect, actionMenu],
    );

    // default item renderer for the middle menu, used in both default and contextual modes
    const renderItem = useCallback(
        (item: SelectMenuItem) => {
            const itemDisabled = (disableSelectOptions && !item.isSelected) || item.isDisabled;
            return (
                // DS wrapper so an option's checked state reaches AT; a menu has no aria-selected, so single-select is a radio.
                <DropdownItem
                    key={item.id}
                    disabled={itemDisabled}
                    role={actionMenu ? 'menuitem' : multiSelect ? 'menuitemcheckbox' : 'menuitemradio'}
                    aria-checked={actionMenu ? undefined : item.isSelected}
                    className={`${middleMenuItemsClassName} ${itemDisabled ? disabledItemClass : enabledItemClass} ${
                        item.variant === 'destruct' ? 'text-system-error' : ''
                    }`}
                    onClick={() => handleItemClick(item)}
                    onKeyDown={(e) => {
                        const action = e.currentTarget.querySelector('button');

                        if (
                            enableActions &&
                            item.onAction &&
                            action &&
                            e.key === 'Tab' &&
                            !e.shiftKey &&
                            e.target === e.currentTarget
                        ) {
                            e.preventDefault();
                            action.focus();
                        }
                    }}
                    testId={item.testId}
                >
                    <SelectItemContent
                        item={item}
                        multiSelect={multiSelect}
                        displaySelectedIcon={displaySelectedIcon}
                        enableActions={enableActions}
                    />
                </DropdownItem>
            );
        },
        [
            disableSelectOptions,
            middleMenuItemsClassName,
            handleItemClick,
            actionMenu,
            multiSelect,
            displaySelectedIcon,
            enableActions,
        ],
    );

    // Suppress the first group's top border when a search input or a Select All divider
    // already provides separation above it, otherwise the two stack into a double line.
    const hasSelectAll = multiSelect && !!onSelectAll;

    const renderGroupHeaders = (index: number) =>
        groupBy ? (
            <div
                className={
                    (searchable || hasSelectAll) && index === 0
                        ? ''
                        : `border-solid border-t border-b-0 border-l-0 border-r-0 border-system-outline-variant ${groupHeaderBorderClassName}`
                }
            >
                <div className={`px-3 py-1 bg-system-surface ${groupHeaderClassName}`}>
                    <Text variant="title-medium" className={`cursor-default ${groupHeaderTextClassName}`}>
                        {renderGroupHeader ? renderGroupHeader(groupKeys[index]) : groupKeys[index]}
                    </Text>
                </div>
            </div>
        ) : null;

    // contextual mode: Custom dropdown with integrated search and grouping
    if (isContextual) {
        return (
            <ContextualDropdown
                id={id}
                value={value}
                label={hideLabel ? undefined : label}
                helper={helper}
                testId={testId}
                disabled={disabled}
                header={header}
                triggerMode={searchable === 'portal' ? 'button' : 'input'}
                ariaLabel={ariaLabel ?? (hideLabel ? label : undefined)}
                searchLabel={searchLabel}
                placeholder={placeholder}
                triggerDisplay={triggerContent}
                renderTrigger={searchable === 'portal' ? renderTrigger : undefined}
                errorMessage={errorMessage}
                invalid={invalid}
                size={size}
                buttonClassName={buttonClassName}
                variant={variant}
                arrow={arrow}
                arrowClassName={arrowClassName}
                onClear={onClear}
                clearLabel={clearLabel}
                middleMenuItems={middleMenuItems}
                bottomMenuItems={bottomMenuItems}
                filteredItems={filteredItems}
                groups={groups}
                groupKeys={groupKeys}
                groupBy={groupBy}
                renderGroupHeader={renderGroupHeader}
                groupHeaderClassName={groupHeaderClassName}
                groupHeaderTextClassName={groupHeaderTextClassName}
                groupHeaderBorderClassName={groupHeaderBorderClassName}
                emptyGroupMessage={emptyGroupMessage}
                searchValue={searchValue}
                onSearchChange={handleSearchChange}
                inputProps={inputProps}
                multiSelect={multiSelect}
                onSelectAll={onSelectAll}
                allSelected={allSelected}
                isSelectAllDisabled={isSelectAllDisabled}
                disableSelectOptions={disableSelectOptions}
                onItemSelect={onItemSelect}
                onEscape={onEscape}
                displaySelectedIcon={displaySelectedIcon}
                middleMenuClassName={middleMenuClassName}
                middleMenuItemsClassName={middleMenuItemsClassName}
                middleMenuStyle={middleMenuStyle}
                dropdownClassName={dropdownClassName}
                dropdownStyle={dropdownStyle}
                dropdownThemeOverride={dropdownThemeOverride}
            />
        );
    }

    // default: standard Flowbite Dropdown with optional search and grouping
    return (
        <div className="flex flex-col gap-1" ref={setStatusHost}>
            {label && (
                <div className="flex flex-row">
                    <Text id={labelId} variant="label-large" className={hideLabel ? 'sr-only' : undefined}>
                        {label}
                        {labelRequiredSymbol && <VisuallyHidden> {__('misc.required')}</VisuallyHidden>}
                    </Text>
                    {labelRequiredSymbol && (
                        <div aria-hidden="true">
                            <Text variant="label-large" className="text-system-error pl-0.5">
                                *
                            </Text>
                        </div>
                    )}
                </div>
            )}
            <Dropdown
                disabled={disabled}
                placement={placement}
                data-testid={testId}
                label={value}
                renderTrigger={renderTrigger ?? dropdownTrigger}
                theme={resolvedTheme}
                dismissOnClick={!multiSelect}
            >
                <div className={dropdownClassName} style={dropdownStyle}>
                    {/* Mounted with the menu, but rendered outside it. */}
                    {searchable === true &&
                        statusHost &&
                        createPortal(
                            <SelectSearchStatus searchValue={searchTerm} resultCount={filteredItems.length} />,
                            statusHost,
                        )}
                    {searchable === true && (
                        <>
                            <div className="px-3 py-2 bg-system-surface">
                                <Input
                                    autoFocus
                                    type="text"
                                    placeholder={searchLabel ?? __('misc.search_placeholder')}
                                    ariaLabel={searchLabel ?? __('misc.search_placeholder')}
                                    value={searchTerm}
                                    onChange={(e) => handleSearchChange(e.currentTarget.value)}
                                    // ArrowDown and Escape reach the menu's list navigation and dismiss; typing stays in the field
                                    onKeyDown={(e) => {
                                        if (e.key !== 'ArrowDown' && e.key !== 'Escape') {
                                            e.stopPropagation();
                                        }
                                    }}
                                    leftIcon="IconMagnifier"
                                    clearable
                                    rightIconLabel={resolvedSearchClearLabel}
                                />
                            </div>
                            <div
                                className={`border-solid border-b border-t-0 border-l-0 border-r-0 border-system-outline-variant ${searchHeaderBorderClassName}`}
                            />
                        </>
                    )}
                    {header && (
                        <Dropdown.Header>
                            <Text variant="title-large">{header.title}</Text>
                            {header.subtitle && <Text variant="label-large">{header.subtitle}</Text>}
                        </Dropdown.Header>
                    )}
                    {/* role none: a generic wrapper would break the menu's owned-elements chain. */}
                    <div className={`${middleMenuClassName} min-h-0`} style={middleMenuStyle} role="none">
                        {multiSelect && onSelectAll && (
                            <>
                                <DropdownItem
                                    key={`${id}-select-all`}
                                    onClick={handleSelectAll}
                                    disabled={isSelectAllDisabled}
                                    role="menuitemcheckbox"
                                    aria-checked={allSelected}
                                    className={`${middleMenuItemsClassName} ${
                                        isSelectAllDisabled ? disabledItemClass : ''
                                    }`}
                                >
                                    <div className={itemContainerClass}>
                                        {/* Decorative: the row carries the checked state. */}
                                        <CheckIndicator
                                            id={`${id}-select-all`}
                                            checked={allSelected}
                                            className="mr-2"
                                        />
                                        <div className={contentContainerClass}>
                                            <Text variant="label-large">{__('misc.select_all')}</Text>
                                        </div>
                                    </div>
                                </DropdownItem>
                                <Dropdown.Divider />
                            </>
                        )}
                        {groupKeys.map((key, index) => {
                            const groupItems = groups[key] ?? [];
                            return (
                                <React.Fragment key={key}>
                                    {renderGroupHeaders(index)}
                                    {groupBy && groupItems.length === 0 ? (
                                        <SelectEmptyGroup message={resolvedEmptyGroupMessage} />
                                    ) : (
                                        groupItems.map(renderItem)
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                    {bottomMenuItems.length > 0 && (
                        <>
                            <Dropdown.Divider />
                            {bottomMenuItems.map((item) => (
                                <Dropdown.Item
                                    key={item.id}
                                    icon={item.iconName ? undefined : item.icon}
                                    onClick={item.onClick}
                                    className="cursor-pointer text-system-on-surface"
                                >
                                    {item.iconName && (
                                        <IconLoader size="sm" iconName={item.iconName} className="mr-2" />
                                    )}
                                    <Text variant="title-small" className={item.iconName || item.icon ? '' : 'ml-2'}>
                                        {item.value}
                                    </Text>
                                </Dropdown.Item>
                            ))}
                        </>
                    )}
                </div>
            </Dropdown>
            {errorMessage ? (
                <span id={errorId} role="alert">
                    <Text variant="body-small" className="text-system-error">
                        {errorMessage}
                    </Text>
                </span>
            ) : (
                helper && (
                    <Text variant="label-large" className="text-system-on-surface-variant">
                        {helper}
                    </Text>
                )
            )}
        </div>
    );
};
